import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react"
import {
  findInitialVariant, trackTripleWhaleAddToCart,
} from "@utils/product"
import getValue from "get-value"
import { getRegionCurrency, getRegionHandle, getRegionShopUrl, isVisibleIn, mapSizeValue } from "@utils/region"
import { formatPrice, getPricesFromContextualPricing } from "@utils/products/pricing"
import { getPriceFromVariantPresentmentPrices } from "@utils/currency"
import AffirmPromotion from "@components/widgets/affirm-promotion"
import ProductAccordion from "./bundle-product-accordion"
import InlineCrossSell from "@src/components/inline-cross-sell"
import ScreenReaderText from "../screen-reader-text"
// import MyRegistryWidget from "@components/myregistry-widget"
import ShippingConditions from "@components/product/shipping-conditions"
import { getUriFromDocument } from "@src/utils/routing"
import { trackAddToCart, trackEvent } from "@src/services/analytics"
import { getImageUrl } from "@src/utils/image"
import { useCartStore, useQuickViewStore, useProductStore } from "@src/hooks/useStore"
import useToggleStates from "@src/hooks/useToggleStates"
import ReviewsTeaser from "../reviews/reviews-teaser"
import CrateSizeModal from "@components/crate-size-modal"
import Link from "@components/link"
import { v4 as uuidv4 } from 'uuid';
import ReadMore from "@components/read-more"

const revolPId = "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0LzEzMzkyMTkzNzgyNjM="
const snoozPId = "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0LzEzMzkyMzgxMjE1NTk="
const pilloPId = "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0LzcxNTk0Nzk1OTkyOTA="

const BundleAddToCart = ({
  bundleProductsList,
  productEntry,
  fromList,
  sanityDiscountCodes,
  showInlineCrossell = true
}) => {
  const { addLineItems, loading } = useCartStore(["addLineItems", "loading"]);
  const {
    setQuickViewProductList,
  } = useQuickViewStore(["setQuickViewProductList"]);
  const { addedCrossSellProducts, removeAddedCrossSellProducts } = useProductStore(['addedCrossSellProducts', 'removeAddedCrossSellProducts'])
  const { toggleAllFunction } = useToggleStates();
  const [isSizeDetailsOpen, setIsSizeDetailsOpen] = useState(false)
  const uniqueBundleId = uuidv4();

  useEffect(() => {
    setQuickViewProductList(fromList || '/collections/bundles/')
  }, [fromList])

  const initialProduct = useMemo(() => ({ ...bundleProductsList[0] }), [bundleProductsList])
  const currentProduct = initialProduct;

  const initialVariant = useMemo(() => findInitialVariant(currentProduct.variants) || currentProduct.variants?.[0], [currentProduct])

  const initialVariantsForProductLayout = useMemo(() =>
    bundleProductsList
      .map(bundleProduct => {
        const availableVariants = bundleProduct.variants?.filter(variant => variant.isAvailableInRegion);
        const initialVariant = findInitialVariant(availableVariants) || availableVariants?.[0];

        if (!initialVariant) return;

        return {
          ...initialVariant,
          selected: !bundleProduct.isOptional,
          isOptional: bundleProduct.isOptional,
          isMainProduct: bundleProduct.isMainProduct,
        };
      })
      .filter(Boolean),
    [bundleProductsList]
  );

  const initialVariantsForBundles = useMemo(() => bundleProductsList
    .map(bundleProduct => ({
      ...findInitialVariant(bundleProduct.variants) || bundleProduct.variants?.[0],
      selected: !bundleProduct.isOptional,
      isOptional: bundleProduct.isOptional,
      isMainProduct: bundleProduct.isMainProduct,
    }))
    , [bundleProductsList]);

  const initialVariants = currentProduct?.layout === "product" && currentProduct?.isMainProduct ? initialVariantsForProductLayout : initialVariantsForBundles
  const [currentVariant, setCurrentProductVariant] = useState(initialVariant);
  const [selectedVariants, setSelectedVariants] = useState(initialVariants);

  const productTitle = initialVariants.length > 1 ? productEntry?.title : bundleProductsList[0]?.titleForBundle
  const initialCrossSellProducts = []
  const crossSellProducts = initialCrossSellProducts;

  const minVariantsPrice = bundleProductsList?.map(product => getValue(product, "priceFrom"))

  const overallBundleRating = productEntry?.layout === "product"
    ? productEntry?.rating != null
      ? parseFloat(productEntry.rating.toFixed(1))
      : null
    : bundleProductsList?.length
      ? parseFloat((
        bundleProductsList.reduce((sum, product) => sum + (product.rating || 0), 0) / bundleProductsList.length
      ).toFixed(1))
      : null;

  const overallBundleReviewCount = bundleProductsList?.reduce((sum, product) => sum + product.reviewCount, 0)

  const isClassicHarness =
    productEntry?.slug?.current && productEntry?.slug?.current.includes("classic-harness")

  const handleAddToCart = async () => {
    let lines = [];
    const lineItemsData = [];
    selectedVariants?.filter(selectedVariant => selectedVariant.selected)?.map((productVariant) => {
      const product = bundleProductsList.find(bundleProduct => bundleProduct.variants.find(variant => variant.sku === productVariant.sku));
      trackTripleWhaleAddToCart(productVariant.shopifyId);
      if (productVariant.isAvailableInRegion && productVariant.availableForSale) {
        if (productVariant.variantComponents?.length > 0) {
          const componentId = Math.random();
          productVariant.variantComponents.map(variantComponent => {
            const { shopifyIdBase64Encoded, status } = variantComponent;

            lineItemsData.push({
              merchandise: {
                id: shopifyIdBase64Encoded,
                sku: variantComponent.sku,
                title: variantComponent.variantTitle,
                price: variantComponent.price,
                compareAtPrice: variantComponent.compareAtPrice,
                image: {
                  src: getImageUrl(variantComponent.listingImage),
                },
                product: {
                  id: product.shopifyId,
                  title: productTitle,
                  category: product?.productGroups?.map(group => group.title),
                  list: fromList || '/collections/bundles/',
                  onlineStoreUrl: getRegionShopUrl() + getUriFromDocument(product),
                  productWithGift: productEntry?.layout ? "true" : "false"
                },
              },
              quantity: 1,
            })

            lines.push({
              merchandiseId: shopifyIdBase64Encoded,
              attributes: {
                _preorder: status?.preorder ? "true" : "false",
                _backorder: status?.backorder ? "true" : "false",
                _region: getRegionHandle(),
                _bundle: productEntry?._id,
                _bundleUniqueId: uniqueBundleId,
                _componentOf: componentId.toString(36),
                _category: product?.productGroups?.map(group => group.title).join(', '),
                _list: fromList || '/collections/bundles/',
                _productWithGift: productEntry?.layout ? "true" : "false",
              },
              quantity: 1,
            });
          });
        } else {
          lineItemsData.push({
            merchandise: {
              id: productVariant.shopifyId,
              sku: productVariant.sku,
              title: productVariant.variantTitle,
              price: (+productVariant.price)?.toFixed(2),
              compareAtPrice: (+productVariant.compareAtPrice)?.toFixed(2),
              image: {
                src: getImageUrl(productVariant.listingImage),
              },
              product: {
                id: product.shopifyId,
                title: productTitle,
                category: product?.productGroups?.map(group => group.title),
                list: fromList || '/collections/bundles/',
                onlineStoreUrl: getRegionShopUrl() + getUriFromDocument(product),
                productWithGift: productEntry?.layout ? "true" : "false",
              },
            },
            quantity: product?.discountQuantity || 1,
          })

          lines.push({
            merchandiseId: productVariant.shopifyIdBase64Encoded,
            attributes: {
              _preorder: productVariant.status?.preorder ? "true" : "false",
              _backorder: productVariant.status?.backorder ? "true" : "false",
              _region: getRegionHandle(),
              _bundle: productEntry?._id,
              _bundleUniqueId: uniqueBundleId,
              _category: product?.productGroups?.map(group => group.title).join(', '),
              _list: fromList || '/collections/bundles/',
              _productWithGift: productEntry?.layout ? "true" : "false",
            },
            quantity: product?.discountQuantity || 1,
          })
        }
      }
    });

    if (Object.keys(addedCrossSellProducts).length !== 0) {
      addedCrossSellProducts.forEach(({ product, quantity }) => {
        trackTripleWhaleAddToCart(product.shopifyId);

        lineItemsData.push({
          merchandise: {
            id: product.shopifyId,
            sku: product.sku,
            title: product.variantTitle,
            price: Number(product.price)?.toFixed(2),
            compareAtPrice: Number(product.compareAtPrice)?.toFixed(2),
            image: {
              src: getImageUrl(product.variantImages[0]),
            },
            product: {
              id: product.product.id,
              title: productTitle,
              category: product.product.category.join(', '),
              list: fromList || '/collections/bundles/',
              onlineStoreUrl: product.product.onlineStoreUrl,
              productWithGift: productEntry?.layout ? true : false,
            },
          },
          quantity,
        });

        lines.push({
          merchandiseId: product.shopifyIdBase64Encoded,
          attributes: {
            ...product.attributes,
          },
          quantity,
        })
      })
    }
    await addLineItems(lines);
    toggleAllFunction("drawer", true)
    removeAddedCrossSellProducts();

    setSelectedVariants(prevState => {
      return prevState.map(variant => ({ ...variant, selected: !variant.isOptional }))
    });

    trackAddToCart(lineItemsData, fromList || '/collections/bundles/')
  }

  const updateSelectedVariants = useCallback((index, _currentVariant, isRevol) => {
    if (!_currentVariant) return;

    const newSelectedVariants = selectedVariants?.map((previousVariant, _index) => {
      if (index === _index) return _currentVariant;
      else return previousVariant;
    });

    setSelectedVariants(isRevol ? mapProductSize(newSelectedVariants) : newSelectedVariants);
  }, [selectedVariants])

  /**
   * Map Snooz and Pillo size to be same as Revol's
   */
  const mapProductSize = useCallback((selectedVariants) => {
    const revol = bundleProductsList.find(product => product.shopifyId == revolPId)
    const snooz = bundleProductsList.find(product => product.shopifyId === snoozPId)
    const pillo = bundleProductsList.find(product => product.shopifyId === pilloPId)

    if (revol) {
      const selectedRevol =
        selectedVariants.find(variant => variant.productId === revol?.shopifyId)

      if (!selectedRevol) return

      const { size: matchingSize } = selectedRevol
      const revolIndex = selectedVariants.indexOf(selectedRevol)

      let mappedSelectedVariants = selectedVariants
        .filter(variant => variant !== selectedRevol)
        ?.map((variant) => {
          const matchingProduct = bundleProductsList.find(
            ({ shopifyId }) => variant.productId === shopifyId
          )

          if (!matchingProduct) return variant

          const { color: selectedColor } = variant

          let sizeVariantMatches = []

          if ((snooz || pillo) && matchingSize) {
            sizeVariantMatches = matchingProduct.variants
              .filter(
                (variant) =>
                  mapSizeValue(variant.size) === matchingSize &&
                  !variant.hide &&
                  isVisibleIn(variant.visibilityRegions) &&
                  variant.availableForSale
              )?.find(sizeVariantMatch => sizeVariantMatch.color === selectedColor)
          }

          // Products without matching size will fall back to the default variant
          if (!sizeVariantMatches || sizeVariantMatches.length === 0) return variant

          return sizeVariantMatches
        })

      mappedSelectedVariants.splice(revolIndex, 0, selectedRevol)

      return mappedSelectedVariants
    }

    return selectedVariants
  }, [revolPId, snoozPId, pilloPId]);
  const isProductInRegion = useMemo(() => {
    return (
      (productEntry?.isAvailableInRegion ?? true) &&
      bundleProductsList.every((product) => isVisibleIn(product.isAvailableInRegion)) &&
      selectedVariants.every((_var) => _var?.isAvailableInRegion) &&
      crossSellProducts?.every((_var) => _var?.isAvailableInRegion)
    );
  }, [productEntry, bundleProductsList, selectedVariants, crossSellProducts]);

  const [isAvailableForSale, setAvailability] = useState(
    selectedVariants.every(_var => _var?.availableForSale)
  )

  useEffect(() => {
    if (crossSellProducts?.length > 0) {
      setAvailability(crossSellProducts?.every((_var) => _var?.availableForSale))
    }
  }, [crossSellProducts]);

  const mandatoryItemsAdded = useMemo(() =>
    selectedVariants.length >= bundleProductsList.filter((product) => !product?.isOptional).length || (currentProduct?.layout === "product" && currentProduct?.isMainProduct),
    [selectedVariants])

  const unavailable = useMemo(() =>
    !mandatoryItemsAdded
    || !isAvailableForSale
    || loading
    || !isProductInRegion
    , [selectedVariants, isAvailableForSale, loading, isProductInRegion])

  useEffect(() => {
    setAvailability(selectedVariants.every(_var => _var?.availableForSale));
  }, [selectedVariants]);

  const currencyOptionPrice = useMemo(() => {
    const allPrices = selectedVariants.map((productVariant, index) => {
      const product = bundleProductsList.find((product, _index) => product?.shopifyId === productVariant?.productId && _index === index);
      const discountAmount = (price) => product?.discountType === "percentage" ? price - (price * (+product?.discountAmount / 100)) : price - +product?.discountAmount;
      const discountQuantity = product?.discountQuantity || 1;
      const { price, compareAtPrice, currency } = getPricesFromContextualPricing(productVariant?.prices)

      const prices = {
        price: {
          currencyCode: currency,
          amount: productVariant?.selected
            ? discountAmount(+price * discountQuantity) : 0
        },
        compareAtPrice: {
          currencyCode: currency,
          amount: productVariant?.selected
            ? +compareAtPrice?.amount !== null
              ? +compareAtPrice * discountQuantity
              : +price * discountQuantity
            : 0
        }
      }

      return prices
    })

    const allFallbackPrices = selectedVariants.map(productVariant => ({
      price: productVariant?.selected ? parseFloat(productVariant?.price) : 0,
      compareAtPrice: productVariant?.selected ? parseFloat(productVariant?.compareAtPrice) : 0,
    }))

    const priceValue = allPrices.map((prices, index) => getPriceFromVariantPresentmentPrices(
      [prices.price],
      allFallbackPrices[index]?.price
    ))
    const compareAtPriceValue = allPrices.map((prices, index) => getPriceFromVariantPresentmentPrices(
      [prices.compareAtPrice],
      allFallbackPrices[index]?.compareAtPrice
    ))

    return {
      price: priceValue.reduce((total, itemPrice) => total + itemPrice, 0),
      compareAtPrice: compareAtPriceValue.reduce((total, itemPrice) => total + itemPrice, 0),
    }
  }, [currentVariant, selectedVariants])

  const crossSellTotalPrice = useMemo(() => {
    return addedCrossSellProducts.map(crossSell => crossSell.product.price * crossSell.quantity).reduce((a, b) => a + b, 0);
  }, [currentVariant, addedCrossSellProducts])

  const totalPrice = currencyOptionPrice?.price
  const discountedPrice = currencyOptionPrice?.compareAtPrice
  const accumulatedPrice = totalPrice + crossSellTotalPrice;

  const totalFormattedPrice = formatPrice({
    price: totalPrice,
    currency: getRegionCurrency() || minVariantsPrice[0].currencyCode,
    minimumFractionDigits: Number.isInteger(totalPrice) ? 0 : 2,
  })

  const discountedFormattedPrice = formatPrice({
    price: discountedPrice,
    currency: getRegionCurrency() || minVariantsPrice[0].currencyCode,
    minimumFractionDigits: Number.isInteger(discountedPrice) ? 0 : 2,
  })

  const accumulatedFormattedPrice = formatPrice({
    price: accumulatedPrice,
    currency: getRegionCurrency() || minVariantsPrice[0].currencyCode,
    minimumFractionDigits: Number.isInteger(accumulatedPrice) ? 0 : 2,
  })

  const trackUpsellClicks = () => {
    trackEvent("upsell_click", {});
  }

  const hasSizeGuide = productEntry?.crateSize?.sizes?.length > 0

  const closeSizeDetails = () => {
    setIsSizeDetailsOpen(false)
  }

  const openSizeDetails = useCallback(() => {
    setIsSizeDetailsOpen(true)
  }, [setIsSizeDetailsOpen])

  return (
    <React.Fragment>
      <div className="flex flex-col gap-y-5">
        <div className="hidden md:flex flex-col items-stretch">
          <div className="flex w-full items-start gap-1 justify-between">
            <h1 aria-label="Product Title" className="text-[40px]">
              <ScreenReaderText srText={`Product title: ${productTitle}`} className="text-inherit" defaultText={productTitle} />
            </h1>

            {totalFormattedPrice && <span className="headline text-[40px]" aria-label="Discounted Price">
              <ScreenReaderText srText={`Discounted Price: ${totalFormattedPrice}`} className="text-inherit" defaultText={totalFormattedPrice} />
            </span>}
          </div>

          <div className="flex items-center justify-between pt-2">
            <ReviewsTeaser rating={overallBundleRating} reviewCount={overallBundleReviewCount} starsFillColor={"#191818"} isPDP={true} />
            {discountedFormattedPrice && discountedPrice > totalPrice && <div className="flex items-center gap-x-5">
              <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2" aria-label="Original Price">
                <ScreenReaderText srText={`Original Price: ${discountedFormattedPrice}`} defaultText={discountedFormattedPrice} />
              </p>
            </div>}
          </div>
        </div>

        <div className="flex md:hidden flex-col items-stretch gap-y-2.5 pt-4 md:pt-0">
          <div className="flex flex-col gap-y-2.5">
            <ReviewsTeaser rating={overallBundleRating} reviewCount={overallBundleReviewCount} starsFillColor={"#191818"} isPDP={true} />
            <h1 aria-label="Product Title" className="text-[32px]">
              <ScreenReaderText srText={`Product title: ${productTitle}`} defaultText={productTitle} />
            </h1>
          </div>

          <div className="flex flex-col gap-y-2.5">
            {totalFormattedPrice && <span className="headline text-[24px]" aria-label="Discounted Price">
              <ScreenReaderText srText={`Discounted Price: ${totalFormattedPrice}`} defaultText={totalFormattedPrice} />
            </span>}
            {discountedFormattedPrice && discountedPrice > totalPrice && <div className="flex items-center gap-x-2.5">
              <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2" aria-label="Original Price">
                <ScreenReaderText srText={`Original Price: ${discountedFormattedPrice}`} defaultText={discountedFormattedPrice} />
              </p>
            </div>}
          </div>
        </div>
        {productEntry?.layout === "product" && productEntry?.summary && <ReadMore text={productEntry?.summary} />}
      </div>
      <dl className="flex flex-col gap-2.5 md:gap-5 pt-2.5 md:pt-5">
        <ProductAccordion {...
          {
            bundleProductsList,
            setCurrentProductVariant,
            updateSelectedVariants,
            revolPId,
            selectedVariants,
            mainLayout: productEntry?.layout,
            crateSize: productEntry?.crateSize,
            sanityDiscountCodes
          }
        } />
      </dl>
      {isClassicHarness && productEntry?.layout == "product" && (
        <div className="flex justify-end">
          <Link
            onClick={openSizeDetails}
            className="hover:text-safety-green focus:outline-none transition ease-in-out duration-150 antialiased font-medium underline"
          >
            View Size Guide
          </Link>
        </div>
      )}
      <ShippingConditions {...{ productEntry, currentEntryVariant: selectedVariants.find(variant => variant.isMainProduct) }} className="pt-5" />
      {productEntry.crossSell && showInlineCrossell && (
        <div className="pt-5 upsell-variant-A">
          <InlineCrossSell
            {...{ products: productEntry.crossSell, trackUpsellClicks }}
          />
        </div>
      )}

      <div className="flex flex-col py-5 gap-2.5 md:gap-5 sticky relative bottom-0 bg-white z-[1]">
        <div className="flex gap-2.5 md:gap-5">
          <button
            disabled={unavailable}
            onClick={handleAddToCart}
            type="button"
            className={`btn btn-secondary hover:btn-primary w-full is-sticky-sm disabled:opacity-70 disabled:cursor-not-allowed`}
          >
            {loading ? "Adding" : 'Add to Cart'}
            <span className="price is-sticky-price">{accumulatedFormattedPrice}</span>
          </button>
        </div>

        {!isProductInRegion &&
          <p>This product is not yet available in your country.</p>}
        <AffirmPromotion total={totalPrice} type="product" />
      </div>

      {/* <MyRegistryWidget {...
      {
        product: productEntry,
        totalPrice,
        discountedPrice,
        siteSettings,
        availablility: selectedVariants.some((_var) => !_var.availableForSale) || !isProductInRegion,
      }} --> */}
      {hasSizeGuide && productEntry?.layout == "product" && (
        <CrateSizeModal
          isOpen={isSizeDetailsOpen}
          toggle={closeSizeDetails}
          items={productEntry?.crateSize?.sizes}
          quizLink={productEntry?.crateSize?.quizLink}
        />
      )}
    </React.Fragment>
  )
}
export default BundleAddToCart
